/**
 * Identité légale de l'entreprise (décisions du propriétaire du 17 et du
 * 18 octobre 2026) : raison sociale, forme juridique, capital, immatriculation,
 * SIRET, TVA, code APE et adresse du siège. Le patron seul la modifie, après
 * une vérification d'identité récente ; chaque modification est auditée avec
 * l'ancienne et la nouvelle valeur, et les documents déjà émis gardent celle
 * qu'ils portaient.
 *
 * La raison sociale et le SIRET vivent sur la fiche de l'entreprise ; les
 * autres mentions dans son identité des documents (`TenantBranding`).
 */

import { z } from 'zod';

/** Mentions légales rangées dans l'identité des documents, et leur longueur maximale. */
export const LEGAL_BRANDING_FIELDS = [
  'legalForm', 'shareCapital', 'registration', 'vatNumber', 'apeCode', 'documentAddress',
] as const;
export type LegalBrandingField = (typeof LEGAL_BRANDING_FIELDS)[number];

export const LEGAL_IDENTITY_LIMITS = {
  name: 200,
  legalForm: 80,
  shareCapital: 40,
  registration: 120,
  vatNumber: 30,
  apeCode: 10,
  address: 500,
} as const;

/** Identité légale telle que l'écran la lit et l'écrit. */
export interface LegalIdentity {
  name: string;
  siret: string | null;
  legalForm: string;
  shareCapital: string;
  registration: string;
  vatNumber: string;
  apeCode: string;
  /** Adresse du siège, imprimée sur les documents. */
  address: string;
}

/** Espaces retirés : « 123 456 789 00012 » devient « 12345678900012 ». */
export function normalizeSiret(value: string): string {
  return value.replace(/[\s.]/g, '');
}

/**
 * SIRET plausible : 14 chiffres et clé de Luhn. Les établissements de La Poste
 * (SIREN 356 000 000) suivent une autre règle : somme des chiffres multiple de 5.
 */
export function isValidSiret(value: string): boolean {
  if (!/^\d{14}$/.test(value)) return false;
  if (value.startsWith('356000000')) {
    return [...value].reduce((sum, digit) => sum + Number(digit), 0) % 5 === 0;
  }
  let sum = 0;
  for (let index = 0; index < 14; index += 1) {
    let digit = Number(value[13 - index]);
    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/**
 * SIRET saisi (espaces et points tolérés), rendu normalisé et vérifié : même
 * règle pour le patron et pour la console de la plateforme.
 */
export const siretSchema = z
  .string()
  .trim()
  .max(20)
  .transform(normalizeSiret)
  .refine(isValidSiret, 'SIRET invalide : 14 chiffres et clé de contrôle.');

/**
 * Téléphone de l'entreprise montré aux clients (documents, QR, suivi, portail) :
 * vide pour le retirer, sinon 6 à 20 caractères parmi chiffres, espaces et `+.()-`.
 */
export const publicPhoneSchema = z
  .string()
  .trim()
  .max(20)
  .refine((value) => value === '' || /^\+?[0-9 .()-]{6,20}$/.test(value), 'Numéro de téléphone invalide.');
